import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { useDispatch, useSelector } from "react-redux";
import { fetchUserRoute } from "../../store/slices/attendanceSlice";
import { RootDispatch, RootState } from "../../store";
import {
  MapContainer,
  TileLayer,
  Marker,
  Polyline,
  Tooltip,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import socket from "../../utils/socketConnect.utils";
import { haversineDistance } from "../../utils/haversine";

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

interface Coordinate {
  lat: number;
  lng: number;
  timestamp: string;
}

interface Segment {
  from: Coordinate;
  to: Coordinate;
  distance: number;
}

interface ShowUserRouteMapProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string | null;
}

export const ShowUserRouteMap: React.FC<ShowUserRouteMapProps> = ({
  isOpen,
  onClose,
  userId,
}) => {
  const [coords, setCoords] = useState<Coordinate[]>([]);
  const [loading, setLoading] = useState(false);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [totalDistanceKm, setTotalDistanceKm] = useState<number>(0);

  const dispatch = useDispatch<RootDispatch>();
  const routeData = useSelector(
    (state: RootState) => state.attendance.routeData
  );

  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  });

  useEffect(() => {
    if (userId && isOpen) {
      const today = new Date().toISOString().split("T")[0];
      const dateToUse = selectedDate || today;

      setLoading(true);
      dispatch(fetchUserRoute({ userId, date: dateToUse })).finally(() =>
        setLoading(false)
      );
    }
  }, [userId, isOpen, selectedDate, dispatch]);

  useEffect(() => {
    if (routeData && Array.isArray(routeData)) {
      const normalized: Coordinate[] = routeData.map((d: any) => ({
        lat: d.latitude ?? d.lat,
        lng: d.longitude ?? d.lng,
        timestamp: d.timestamp ?? "",
      }));
      setCoords(normalized);
    }
  }, [routeData]);

  useEffect(() => {
    if (!userId) return;
    if (!socket.connected) socket.connect();

    const channel = `loc-${userId}`;

    const handleLiveLocation = (data: any) => {
      const location = data?.location;
      const timestamp = data?.timestamp;

      if (!location || !location.lat || !location.lng) return;

      const newPoint: Coordinate = {
        lat: location.lat,
        lng: location.lng,
        timestamp: timestamp ?? new Date().toISOString(),
      };

      setCoords((prev) => [...prev, newPoint]);
    };

    socket.on(channel, handleLiveLocation);
    return () => {
      socket.off(channel, handleLiveLocation);
    };
  }, [userId]);

  useEffect(() => {
    if (coords.length < 2) {
      setTotalDistanceKm(0);
      setSegments([]);
      return;
    }

    let total = 0;
    const segs: Segment[] = [];

    for (let i = 1; i < coords.length; i++) {
      const from = coords[i - 1];
      const to = coords[i];
      const dist = haversineDistance(from.lat, from.lng, to.lat, to.lng);
      total += dist;
      segs.push({ from, to, distance: parseFloat(dist.toFixed(2)) });
    }

    setSegments(segs);
    setTotalDistanceKm(parseFloat(total.toFixed(2)));
  }, [coords]);

  const latestCoord = coords.length > 0 ? coords[coords.length - 1] : null;

  const PanToLatest = () => {
    const map = useMap();
    useEffect(() => {
      if (latestCoord) {
        map.panTo([latestCoord.lat, latestCoord.lng]);
      }
    }, [latestCoord]);
    return null;
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="relative w-[90vw] h-[90vh] flex flex-col overflow-hidden rounded-lg bg-white p-4 shadow-xl">
          <button
            onClick={onClose}
            className="absolute top-1.5 right-1 text-black text-4xl font-bold hover:text-gray-700 z-10"
            aria-label="Close"
          >
            ×
          </button>

          <div className="flex justify-between items-center mb-1 pr-20">
            <DialogTitle className="text-lg font-bold">
              User Movement Route
            </DialogTitle>

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <label htmlFor="date" className="text-sm font-medium">
                  Date:
                </label>
                <input
                  type="date"
                  id="date"
                  className="border px-2 py-1 rounded text-sm"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                />
              </div>

              <div className="text-sm font-semibold text-blue-600">
                Total Distance: {totalDistanceKm} km
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-[300px] bg-gray-100 rounded-lg border border-gray-300">
              <p className="text-sm text-gray-1000 italic">Loading...</p>
            </div>
          ) : coords.length === 0 ? (
            <div className="flex items-center justify-center h-[300px] bg-gray-100 rounded-lg border border-gray-300">
              <p className="text-sm text-gray-1000 italic">
                No route data found.
              </p>
            </div>
          ) : (
            <div className="flex-1 overflow-hidden rounded-lg border mt-2">
              <MapContainer
                key={userId}
                center={[coords[0].lat, coords[0].lng]}
                zoom={16}
                scrollWheelZoom
                style={{ height: "100%", width: "100%" }}
              >
                <PanToLatest />
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                />

                <Polyline
                  positions={coords.map((c) => [c.lat, c.lng])}
                  pathOptions={{ color: "blue" }}
                />

                {coords.map((c, i) => (
                  <Marker position={[c.lat, c.lng]} key={i}>
                    <Tooltip
                      direction="top"
                      offset={[0, -10]}
                      opacity={1}
                      permanent
                    >
                      #{i + 1}
                    </Tooltip>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          )}
        </DialogPanel>
      </div>
    </Dialog>
  );
};
